/**
 * Monitor selector hook. Mirrors `ControlMessage::MonitorList` / `MonitorSelect` on the host.
 *
 * Lifecycle:
 *   * Subscribes to the `control` DataChannel and listens for `monitor_list` messages.
 *   * Exposes the current list + the `active` index so the UI can render a chip.
 *   * `select(index)` sends `monitor_select` back to the host.
 *
 * Why a dedicated hook (rather than wiring straight into `useClipboardSync`)?
 *   * Single-responsibility — clipboard is text-only; this is enumeration + selection.
 *   * Tests can mock the hook without touching clipboard logic.
 *   * Easier to delete if the multi-monitor feature is ever rolled back.
 */

import { useCallback, useEffect, useState } from 'react';
import { sendControl, type ControlMessage, type MonitorInfo } from '../clipboard/protocol';

export interface MonitorPickerApi {
  /** Latest monitor list received from the host. Empty until the first `monitor_list` lands. */
  monitors: MonitorInfo[];
  /** Request a monitor switch. No-op if the index isn't in the list or the channel is closed. */
  select: (index: number) => void;
}

export function useMonitorPicker(controlChannel: RTCDataChannel | null): MonitorPickerApi {
  const [monitors, setMonitors] = useState<MonitorInfo[]>([]);

  useEffect(() => {
    if (!controlChannel) return;
    const onMessage = (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      let parsed: ControlMessage;
      try {
        parsed = JSON.parse(ev.data) as ControlMessage;
      } catch {
        return;
      }
      if (parsed.type === 'monitor_list') {
        setMonitors(parsed.monitors);
      }
    };
    controlChannel.addEventListener('message', onMessage);
    return () => controlChannel.removeEventListener('message', onMessage);
  }, [controlChannel]);

  const select = useCallback(
    (index: number) => {
      const target = monitors.find((m) => m.index === index);
      if (!target) return;
      sendControl(controlChannel, { type: 'monitor_select', index });
      // Optimistic update — flag the new monitor as active locally; the host will echo the
      // refreshed list within ~150 ms and reconcile.
      setMonitors((list) => list.map((m) => ({ ...m, active: m.index === index })));
    },
    [controlChannel, monitors],
  );

  return { monitors, select };
}
